import { z } from 'zod';
import { isContainerKind, type RequestKind } from './kind.js';

// WHY (#1446): CONTAINER requests dropped the inline Roads/Packaging
//   selectors from the form, so a CONTAINER write must never carry a
//   NON-EMPTY road/packaging selection. The rejection lives in one place
//   so the create and draft-save schemas share an identical contract
//   (and `write.ts` stays under the hand-written file-size cap).
//
// INVARIANT: empty / omitted selections stay accepted. The redesigned
//   CONTAINER form preserves any hidden legacy roads/packaging by
//   OMITTING the keys — sending an authoritative `[]` is never required
//   and an absent/empty array is the only legal CONTAINER value.
type ContainerDictionaryRefineInput = {
  readonly kind: RequestKind;
  readonly roadOptionIds?: ReadonlyArray<string> | undefined;
  readonly packagingOptionIds?: ReadonlyArray<string> | undefined;
};

/**
 * @aiPurpose reject a non-empty Roads/Packaging selection on a CONTAINER
 *   write body (#1446). Shared by `CreateRequestInputSchema`,
 *   `DraftSaveInputSchema`, and the kind-present branch of
 *   `PatchRequestInputSchema`.
 * @aiInvariant empty array / omitted key is accepted — the redesigned
 *   CONTAINER form preserves hidden legacy selections by omission and
 *   must never be forced to send an authoritative `[]`.
 */
export const enforceContainerDictionaryInvariants = (
  body: ContainerDictionaryRefineInput,
  ctx: z.RefinementCtx,
): void => {
  if (!isContainerKind(body.kind)) return;
  if (body.roadOptionIds !== undefined && body.roadOptionIds.length > 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'container request must not carry roadOptionIds',
      path: ['roadOptionIds'],
    });
  }
  if (body.packagingOptionIds !== undefined && body.packagingOptionIds.length > 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'container request must not carry packagingOptionIds',
      path: ['packagingOptionIds'],
    });
  }
};
